import React, { useState, useMemo } from "react";
import { Live } from "@/common/model/Live";
import { ENDPOINTS } from "@/constants/api";
import {
  MapPinIcon,
  ClockIcon,
  SignalIcon,
  InformationCircleIcon,
  DevicePhoneMobileIcon,
  SunIcon,
  MoonIcon,
} from "@heroicons/react/24/outline";
import { VideoCameraIcon } from "@heroicons/react/24/solid";
import { getDaylightInfo } from "@/utils/sunCalc";

// Default profile picture as a data URL (gray circle with user icon)
const DEFAULT_PROFILE_PIC = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%236b7280'%3E%3Ccircle cx='12' cy='12' r='12' fill='%231f2937'/%3E%3Cpath d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z' fill='%236b7280'/%3E%3C/svg%3E";

// Color function based on max jump height (meters) - matching app pin colors
const getColorByHeight = (height: number): string => {
  if (height < 5)  return "#00E676";  // pin1 - Green
  if (height < 8)  return "#FFEB3B";  // pin2 - Yellow
  if (height < 12) return "#FF9800";  // pin3 - Orange
  if (height < 15) return "#FF5252";  // pin4 - Red
  if (height < 20) return "#FF4081";  // pin5 - Pink
  return "#7C4DFF";                    // pin6 - Purple (20m+)
};

// Kite icon SVG component
const KiteIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2L4 12l8 10 8-10L12 2zm0 3.5L17.5 12 12 18.5 6.5 12 12 5.5z"/>
  </svg>
);

// Watch icon SVG component
const WatchIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
  </svg>
);

// Android icon SVG component
const AndroidIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48C13.85 1.23 12.95 1 12 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31C6.97 3.26 6 5.01 6 7h12c0-1.99-.97-3.75-2.47-4.84zM10 5H9V4h1v1zm5 0h-1V4h1v1z"/>
  </svg>
);

// Apple icon SVG component
const AppleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

// Country code to name mapping for common kitesurfing countries
const COUNTRY_NAMES: Record<string, string> = {
  AE: "United Arab Emirates", AF: "Afghanistan", AL: "Albania", AM: "Armenia", AO: "Angola",
  AR: "Argentina", AT: "Austria", AU: "Australia", AZ: "Azerbaijan", BA: "Bosnia and Herzegovina",
  BB: "Barbados", BD: "Bangladesh", BE: "Belgium", BG: "Bulgaria", BH: "Bahrain",
  BM: "Bermuda", BN: "Brunei", BO: "Bolivia", BR: "Brazil", BS: "Bahamas",
  BZ: "Belize", CA: "Canada", CH: "Switzerland", CL: "Chile", CN: "China",
  CO: "Colombia", CR: "Costa Rica", CU: "Cuba", CV: "Cape Verde", CY: "Cyprus",
  CZ: "Czech Republic", DE: "Germany", DK: "Denmark", DO: "Dominican Republic", DZ: "Algeria",
  EC: "Ecuador", EE: "Estonia", EG: "Egypt", ES: "Spain", FI: "Finland",
  FJ: "Fiji", FR: "France", GB: "United Kingdom", GE: "Georgia", GH: "Ghana",
  GR: "Greece", GT: "Guatemala", HK: "Hong Kong", HN: "Honduras", HR: "Croatia",
  HU: "Hungary", ID: "Indonesia", IE: "Ireland", IL: "Israel", IN: "India",
  IQ: "Iraq", IR: "Iran", IS: "Iceland", IT: "Italy", JM: "Jamaica",
  JO: "Jordan", JP: "Japan", KE: "Kenya", KR: "South Korea", KW: "Kuwait",
  KY: "Cayman Islands", KZ: "Kazakhstan", LB: "Lebanon", LK: "Sri Lanka", LT: "Lithuania",
  LU: "Luxembourg", LV: "Latvia", LY: "Libya", MA: "Morocco", MC: "Monaco",
  MD: "Moldova", ME: "Montenegro", MG: "Madagascar", MK: "North Macedonia", ML: "Mali",
  MM: "Myanmar", MN: "Mongolia", MO: "Macau", MT: "Malta", MU: "Mauritius",
  MV: "Maldives", MX: "Mexico", MY: "Malaysia", MZ: "Mozambique", NA: "Namibia",
  NC: "New Caledonia", NG: "Nigeria", NI: "Nicaragua", NL: "Netherlands", NO: "Norway",
  NP: "Nepal", NZ: "New Zealand", OM: "Oman", PA: "Panama", PE: "Peru",
  PF: "French Polynesia", PH: "Philippines", PK: "Pakistan", PL: "Poland", PR: "Puerto Rico",
  PT: "Portugal", PY: "Paraguay", QA: "Qatar", RO: "Romania", RS: "Serbia",
  RU: "Russia", SA: "Saudi Arabia", SC: "Seychelles", SD: "Sudan", SE: "Sweden",
  SG: "Singapore", SI: "Slovenia", SK: "Slovakia", SN: "Senegal", SO: "Somalia",
  SV: "El Salvador", SY: "Syria", TH: "Thailand", TN: "Tunisia", TR: "Turkey",
  TT: "Trinidad and Tobago", TW: "Taiwan", TZ: "Tanzania", UA: "Ukraine", UG: "Uganda",
  US: "United States", UY: "Uruguay", UZ: "Uzbekistan", VE: "Venezuela", VN: "Vietnam",
  ZA: "South Africa", ZM: "Zambia", ZW: "Zimbabwe",
};

const getCountryName = (alpha2: string | undefined): string => {
  if (!alpha2) return "";
  return COUNTRY_NAMES[alpha2.toUpperCase()] || alpha2.toUpperCase();
};

// Get connection status based on last update time
const getConnectionStatus = (lastGpsUpdate: number | string | undefined, lastJumpUpdate: number | string | undefined): {
  status: "online" | "warning" | "offline";
  color: string;
  bgColor: string;
  textColor: string;
  label: string;
  minutesAgo: number;
} => {
  // Get the most recent update
  const gpsTime = lastGpsUpdate ? (typeof lastGpsUpdate === "string" ? new Date(lastGpsUpdate).getTime() : lastGpsUpdate) : 0;
  const jumpTime = lastJumpUpdate ? (typeof lastJumpUpdate === "string" ? new Date(lastJumpUpdate).getTime() : lastJumpUpdate) : 0;
  const lastUpdate = Math.max(gpsTime, jumpTime);

  if (!lastUpdate) {
    return {
      status: "offline",
      color: "#ef4444",
      bgColor: "bg-red-500/20",
      textColor: "text-red-400",
      label: "Offline",
      minutesAgo: -1,
    };
  }

  const now = Date.now();
  const diffMs = now - lastUpdate;
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) {
    return {
      status: "online",
      color: "#10b981",
      bgColor: "bg-green-500/20",
      textColor: "text-green-400",
      label: "Online",
      minutesAgo: 0,
    };
  } else if (diffMins <= 3) {
    return {
      status: "warning",
      color: "#f59e0b",
      bgColor: "bg-amber-500/20",
      textColor: "text-amber-400",
      label: "Idle",
      minutesAgo: diffMins,
    };
  } else {
    return {
      status: "offline",
      color: "#ef4444",
      bgColor: "bg-red-500/20",
      textColor: "text-red-400",
      label: "Offline",
      minutesAgo: diffMins,
    };
  }
};

// Get device info display
const getDeviceInfo = (device: Live["device"]) => {
  if (!device) return null;

  const os = device.os?.toLowerCase() || "";
  const brand = device.brand?.toLowerCase() || "";
  const model = (device.model || "").toLowerCase();

  const isWatch = os.includes("watch") || os.includes("wear") || brand.includes("garmin") || os.includes("garmin");

  // Check for Apple: brand contains "apple" or iOS indicators
  const isApple = brand.includes("apple") || os.includes("ios") || os.includes("iphone") || model.includes("iphone") || model.includes("ipad");

  let deviceIcon: "phone" | "watch" = "phone";
  // Apple if detected, otherwise default to Android
  let osType: "android" | "ios" = isApple ? "ios" : "android";
  let label = device.brand || "Unknown";

  if (isWatch) {
    deviceIcon = "watch";
    label = device.brand || "Watch";
  }

  return { deviceIcon, osType, label, model: device.model, os: device.os };
};

interface LiveSessionCardProps {
  session: Live;
  isSelected: boolean;
  isHovered: boolean;
  onSelect: () => void;
  onHover: () => void;
  onHoverEnd: () => void;
  onShowDetails: () => void;
}

const LiveSessionCard = React.forwardRef<HTMLDivElement, LiveSessionCardProps>(({
  session,
  isSelected,
  isHovered,
  onSelect,
  onHover,
  onHoverEnd,
  onShowDetails,
}, ref) => {
  const [imgError, setImgError] = useState(false);

  const formatTimestamp = (timestamp: number | string | undefined | null) => {
    if (!timestamp) return "N/A";
    const date = typeof timestamp === "string" ? new Date(timestamp) : new Date(timestamp);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getSessionStartTime = () => {
    if (!session.sessiontime || session.sessiontime <= 0) return "N/A";
    const startTime = new Date(Date.now() - session.sessiontime * 1000);
    return startTime.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTimeTooltip = () => {
    return `Session started: ${getSessionStartTime()}\nLast GPS: ${formatTimestamp(session.lastgpsupdate)}\nLast Jump: ${formatTimestamp(session.lastjumpupdate)}`;
  };

  const profileSrc = imgError || !session.user.profilepicid
    ? DEFAULT_PROFILE_PIC
    : `${ENDPOINTS.IMAGES.PROFILE}/${session.user.profilepicid}`;

  const formatSessionTime = (seconds: number | undefined | null) => {
    if (!seconds || seconds <= 0) {
      return "-";
    }
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    if (minutes > 0) {
      return `${minutes}m`;
    }
    return `${seconds}s`;
  };

  const formatHeight = (height: number) => {
    return height.toFixed(1);
  };

  const getTimeAgo = (timestamp: number | string | undefined | null): string | null => {
    if (timestamp === null || timestamp === undefined) return null;

    const date = typeof timestamp === "string" ? new Date(timestamp) : new Date(timestamp);

    // Check for invalid date
    if (isNaN(date.getTime())) return null;

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();

    // If diff is negative or unreasonably large (> 30 days), treat as invalid
    if (diffMs < 0 || diffMs > 30 * 24 * 60 * 60 * 1000) return null;

    const diffSecs = Math.floor(diffMs / 1000);

    if (diffSecs < 60) return `${diffSecs}s ago`;
    const diffMins = Math.floor(diffSecs / 60);
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  // Get connection status
  const connectionStatus = getConnectionStatus(session.lastgpsupdate, session.lastjumpupdate);

  // Get local time and daylight info for the spot
  const daylightInfo = useMemo(() => {
    if (session.lastknownlat && session.lastknownlong) {
      return getDaylightInfo(session.lastknownlat, session.lastknownlong);
    }
    return null;
  }, [session.lastknownlat, session.lastknownlong]);

  return (
    <div
      ref={ref}
      onClick={onSelect}
      onMouseEnter={onHover}
      onMouseLeave={onHoverEnd}
      className={`
        p-3 rounded-lg cursor-pointer transition-all duration-200
        ${isSelected
          ? "bg-blue-600 ring-2 ring-blue-400"
          : isHovered
          ? "bg-gray-700"
          : "bg-gray-800 hover:bg-gray-700"
        }
      `}
    >
      {/* Top Row - User Info */}
      <div className="flex items-center gap-3">
        <div className="relative">
          <img
            src={profileSrc}
            alt={session.user.name}
            className="w-12 h-12 rounded-full object-cover border-2 border-gray-600"
            onError={() => setImgError(true)}
          />
          {session.isstreaming && (
            <div className="absolute -top-1 -right-1 bg-red-500 rounded-full p-1">
              <VideoCameraIcon className="w-3 h-3 text-white" />
            </div>
          )}
          {session.user.ispro && (
            <div className="absolute -bottom-1 -right-1 bg-yellow-500 text-xs font-bold px-1 rounded text-black">
              PRO
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-white font-semibold truncate">
              {session.user.name}
            </span>
            {session.user.country && (
              <img
                src={`https://flagcdn.com/w20/${session.user.country.toLowerCase()}.png`}
                alt={session.user.country}
                className="w-5 h-auto cursor-help"
                title={getCountryName(session.user.country)}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            )}
            {/* Connection status badge */}
            <span
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${connectionStatus.bgColor} ${connectionStatus.textColor}`}
              title={`Last update: ${getTimeAgo(session.lastgpsupdate) || getTimeAgo(session.lastjumpupdate) || 'N/A'}`}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: connectionStatus.color }}
              />
              {connectionStatus.label}
            </span>
          </div>
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <span className="truncate">{session.user.username}</span>
            {/* Device indicator - only shown for admins */}
            {session.device && (() => {
              const deviceInfo = getDeviceInfo(session.device);
              if (!deviceInfo) return null;
              const tooltip = [deviceInfo.model, deviceInfo.os].filter(Boolean).join(" | ") || deviceInfo.label;
              return (
                <span title={tooltip} className="flex items-center gap-0.5">
                  {/* Device type icon (phone/watch) */}
                  {deviceInfo.deviceIcon === "watch" ? (
                    <WatchIcon className="w-4 h-4 text-orange-400" />
                  ) : (
                    <DevicePhoneMobileIcon className="w-4 h-4 text-gray-400" />
                  )}
                  {/* OS icon (Android/Apple) */}
                  {deviceInfo.osType === "android" && (
                    <AndroidIcon className="w-3.5 h-3.5 text-green-400" />
                  )}
                  {deviceInfo.osType === "ios" && (
                    <AppleIcon className="w-3.5 h-3.5 text-gray-300" />
                  )}
                </span>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Spot Info & Local Time */}
      <div className="mt-2 flex items-center justify-between text-sm">
        <div className="flex items-center gap-1 text-gray-300 min-w-0 cursor-help" title={session.spot ? `${session.spot.name}, ${getCountryName(session.spot.alpha2)}` : "Unknown location"}>
          <MapPinIcon className="w-4 h-4 flex-shrink-0" />
          <span className="truncate">{session.spot?.name || "Unknown location"}</span>
          {session.spot?.alpha2 && (
            <img
              src={`https://flagcdn.com/w20/${session.spot.alpha2.toLowerCase()}.png`}
              alt={session.spot.alpha2}
              className="w-4 h-auto flex-shrink-0 cursor-help"
              title={getCountryName(session.spot.alpha2)}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          )}
        </div>
        {/* Local Time with Sun/Moon indicator */}
        {daylightInfo && (
          <div
            className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs flex-shrink-0 ${
              daylightInfo.isDaylight
                ? daylightInfo.sunPosition === "golden"
                  ? "bg-orange-500/20 text-orange-300"
                  : "bg-yellow-500/20 text-yellow-300"
                : daylightInfo.sunPosition === "twilight"
                ? "bg-purple-500/20 text-purple-300"
                : "bg-blue-900/30 text-blue-300"
            }`}
            title={`Sunrise: ${daylightInfo.sunriseTime} | Sunset: ${daylightInfo.sunsetTime}${
              daylightInfo.hoursUntilSunset
                ? ` | ${daylightInfo.hoursUntilSunset.toFixed(1)}h until sunset`
                : daylightInfo.hoursUntilSunrise
                ? ` | ${daylightInfo.hoursUntilSunrise.toFixed(1)}h until sunrise`
                : ""
            }`}
          >
            {daylightInfo.isDaylight ? (
              <SunIcon className="w-3.5 h-3.5" />
            ) : (
              <MoonIcon className="w-3.5 h-3.5" />
            )}
            <span className="font-medium">{daylightInfo.localTimeString}</span>
          </div>
        )}
      </div>

      {/* Stats Row */}
      <div className="mt-3 grid grid-cols-2 gap-2 text-center">
        <div
          className="bg-gray-900/50 rounded-lg p-2"
          style={{ borderLeft: `3px solid ${getColorByHeight(session.maxheight)}` }}
        >
          <div
            className="text-lg font-bold"
            style={{ color: getColorByHeight(session.maxheight) }}
          >
            {formatHeight(session.maxheight)}m
          </div>
          <div className="text-xs text-gray-400">Max Height</div>
        </div>
        <div
          className="bg-gray-900/50 rounded-lg p-2"
          style={{ borderLeft: `3px solid ${getColorByHeight(session.lastheight)}` }}
        >
          <div
            className="text-lg font-bold"
            style={{ color: getColorByHeight(session.lastheight) }}
          >
            {formatHeight(session.lastheight)}m
          </div>
          <div className="text-xs text-gray-400">Last Jump</div>
        </div>
      </div>

      {/* Bottom Row - Kite & Time Info */}
      <div className="mt-3 flex items-center justify-between text-xs">
        {/* Kite Info - only show if kite is present */}
        <div className="flex items-center gap-1.5">
          {session.kite && session.kite.kitebrand?.brand && (
            <div className="flex items-center gap-1.5 bg-gray-700 px-2 py-1 rounded text-gray-300">
              <KiteIcon className="w-4 h-4 text-cyan-400" />
              <span>
                {session.kite.kitebrand.brand}
                {session.kite.kitemodel?.model && ` ${session.kite.kitemodel.model}`}
                {session.kite.kitesize?.size && ` ${session.kite.kitesize.size}m`}
              </span>
            </div>
          )}
        </div>

        {/* Time Info & Details */}
        <div className="flex items-center gap-2 text-gray-400">
          <div className="flex items-center gap-1 cursor-help" title={getTimeTooltip()}>
            <ClockIcon className="w-4 h-4" />
            <span>{formatSessionTime(session.sessiontime)}</span>
          </div>
          {/* GPS/Jump time indicator */}
          {(() => {
            const gpsTimeAgo = getTimeAgo(session.lastgpsupdate);
            const jumpTimeAgo = getTimeAgo(session.lastjumpupdate);

            if (gpsTimeAgo) {
              // GPS is available
              return (
                <div className="flex items-center gap-1" title="Last GPS update">
                  <SignalIcon className={`w-4 h-4 ${connectionStatus.textColor}`} />
                  <span>{gpsTimeAgo}</span>
                </div>
              );
            } else if (jumpTimeAgo) {
              // GPS not available, use last jump time
              return (
                <div className="flex items-center gap-1" title="GPS unavailable - showing last jump time">
                  <svg className="w-4 h-4 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span className="text-amber-400">{jumpTimeAgo}</span>
                </div>
              );
            } else {
              // No GPS or jump time available
              return (
                <div className="flex items-center gap-1" title="GPS not available">
                  <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m-3.536-3.536a4 4 0 010-5.656m-7.072 7.072a4 4 0 010-5.656m-3.536 9.192a9 9 0 010-12.728" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6" />
                  </svg>
                  <span className="text-red-400">N/A</span>
                </div>
              );
            }
          })()}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onShowDetails();
            }}
            className="flex items-center gap-1 px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded text-gray-300 transition-colors"
            title="View jump details"
          >
            <InformationCircleIcon className="w-4 h-4" />
            <span>Details</span>
          </button>
        </div>
      </div>
    </div>
  );
});

LiveSessionCard.displayName = "LiveSessionCard";

export default LiveSessionCard;
